import { PageContainer, ProTable } from '@ant-design/pro-components';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button } from 'antd';
import { useMemo, useState } from 'react';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import { useQueryParams } from '@/common/hooks/use-query-params';
import DataLogDetailDrawer from '../components/DataLogDetailDrawer';
import {
  queryAtomicMetricMappingsWithMeta,
  queryExpenseEvents,
  queryIncomeEvents,
  querySalaryEvents,
  queryPaymentEvents,
  queryPlanEvents,
} from '../services/operations';
import { createDataLogColumns, dataLogTabList, getDataLogType } from '../utils/data-log-fields';

const queryByType = {
  expense: queryExpenseEvents,
  income: queryIncomeEvents,
  salary: querySalaryEvents,
  payment: queryPaymentEvents,
  plan: queryPlanEvents,
};

const DataLogs = () => {
  const [query, setQuery] = useQueryParams();
  const [currentRecord, setCurrentRecord] = useState(null);
  const { organizationId, resolveOrganizationPath } = useOrganizationPath();
  const activeType = getDataLogType(query.tab);
  const columns = useMemo(() => createDataLogColumns(activeType, setCurrentRecord), [activeType]);
  const { data: mappingSummary = { meta: {} } } = useQuery({
    enabled: Boolean(organizationId && activeType === 'expense'),
    queryFn: () =>
      queryAtomicMetricMappingsWithMeta({
        current: 1,
        organizationId,
        pageSize: 1,
        sourceDomain: 'expense',
      }),
    queryKey: ['settings-metric-mapping-summary', organizationId],
  });
  const unmappedMappingCount = Number(mappingSummary.meta?.unmappedMappingCount || 0);
  const activeTabName = dataLogTabList.find(({ key }) => key === activeType)?.tab;

  return (
    <PageContainer
      content="租户管理员在这里查看领域采集数据，并追溯领域字段与原始采集快照。"
      tabActiveKey={activeType}
      tabList={dataLogTabList}
      onTabChange={(tab) => {
        setCurrentRecord(null);
        setQuery({ tab: getDataLogType(tab) }, { replace: true });
      }}
    >
      {activeType === 'expense' && unmappedMappingCount > 0 ? (
        <Alert
          showIcon
          type="warning"
          description={`当前有 ${unmappedMappingCount} 个费用类型组合尚未配置指标映射，请尽快处理，否则经营列表数据无法统计。`}
          action={
            <Button type="link" href={resolveOrganizationPath('/settings/metrics?tab=mappings')}>
              前往指标映射
            </Button>
          }
        />
      ) : null}
      <ProTable
        key={activeType}
        rowKey="id"
        columns={columns}
        headerTitle={activeTabName}
        request={(params) => queryByType[activeType]({ ...params, organizationId })}
        search={{ labelWidth: 96 }}
        scroll={{ x: 'max-content' }}
      />
      <DataLogDetailDrawer
        logType={activeType}
        open={Boolean(currentRecord)}
        organizationId={organizationId}
        record={currentRecord}
        onClose={() => setCurrentRecord(null)}
      />
    </PageContainer>
  );
};

export default DataLogs;
